import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Only create Supabase client if credentials are available
export const supabase = (supabaseUrl && supabaseAnonKey)
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

// ── Auth ──────────────────────────────────────────────────────────────────────

/**
 * Sign in with Google via Supabase OAuth.
 * Requires Google provider to be configured in Supabase Dashboard →
 * Authentication → Providers → Google.
 */
export async function signInWithGoogle() {
  if (!supabase) {
    console.warn('Supabase not configured - Google sign-in unavailable');
    return;
  }
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin },
  });
  if (error) console.error('Google sign-in error:', error.message);
}

export async function signOut() {
  if (!supabase) {
    console.warn('Supabase not configured - sign-out unavailable');
    return;
  }
  const { error } = await supabase.auth.signOut();
  if (error) console.error('Sign-out error:', error.message);
}

export function onAuthChange(callback) {
  if (!supabase) {
    // No Supabase credentials, return noop unsubscribe function
    return () => {};
  }
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user ?? null);
  });
  return () => subscription.unsubscribe();
}

export async function getSession() {
  if (!supabase) {
    return null;
  }
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user ?? null;
}

// ── Leaderboard ───────────────────────────────────────────────────────────────

/**
 * Submit a completed puzzle score.
 * Only called when a user is authenticated.
 */
export async function submitScore({ userId, userName, stageId, character, english, mode, timeRemaining, movesRemaining }) {
  if (!supabase) {
    console.warn('Supabase not configured - score submission unavailable');
    return;
  }
  const { error } = await supabase.from('leaderboard').insert({
    user_id: userId,
    user_name: userName,
    stage_id: stageId,
    character,
    english,
    mode,
    time_remaining: timeRemaining,
    moves_remaining: movesRemaining,
  });
  if (error) console.error('Score submit error:', error.message);
}

/**
 * Fetch top 10 scores for a stage, ordered by time remaining (highest = fastest solve).
 */
export async function getLeaderboard(stageId) {
  if (!supabase) {
    console.warn('Supabase not configured - leaderboard unavailable');
    return [];
  }
  const { data, error } = await supabase
    .from('leaderboard')
    .select('user_name, time_remaining, moves_remaining, created_at')
    .eq('stage_id', stageId)
    .order('time_remaining', { ascending: false })
    .limit(10);

  if (error) {
    console.error('Leaderboard fetch error:', error.message);
    return [];
  }
  return data ?? [];
}
